// Ready-made website templates shown on the public site (/templates).
// Each entry drives both its gallery card preview and its live demo page at
// /templates/:slug, so adding a template only needs a new object here.


import { SHOP_TEMPLATES } from './shopTemplates';
import { CREATIVE_TEMPLATES } from './creativeTemplates';
// Gallery tabs, in display order. Tabs with no templates yet are hidden.
export const CATEGORIES = ['Business', 'Shop', 'Services', 'Creative', 'Blog & Magazine', 'Food', 'Beauty & Health'];

export const TEMPLATES = [
  {
    slug: 'freelancer',
    name: 'Freelancer',
    category: 'Services',
    layout: 'freelancer',
    tagline: 'Your work, front and centre.',
    description: 'A personal portfolio for freelancers with services, skills, case studies and testimonials.',
    colors: { primary: '#12213a', accent: '#14a8c8', bg: '#f2f8fa' },
    hero: { title: 'Aarav Mehta, freelance product designer', subtitle: 'Websites, apps and brands for growing businesses.' },
    features: ['Services & skills', 'Case studies', 'Client testimonials'],
    items: [],
    popular: true,
    freelancer: {
      firstName: 'Aarav',
      lastName: 'Mehta',
      role: 'Freelance product designer',
      intro: 'I help startups and small businesses turn ideas into clear, friendly websites and apps. Eight years of design work, from first sketch to launch.',
      tools: ['Ux', 'Ui', 'Ai', 'Web'],
      services: [
        { title: 'Web design', text: 'Fast, responsive websites that explain what you do and turn visitors into customers.' },
        { title: 'Product strategy', text: 'Workshops and roadmaps that decide what to build first and why it matters.' },
        { title: 'AI prototyping', text: 'Working prototypes of AI features so you can test ideas with real users early.' },
      ],
      approach: 'Every project starts with your goals and your customers. I research, sketch, test and refine until the design is simple to use and easy to build.',
      approachPoints: ['Clear weekly updates', 'Unlimited revisions during design'],
      skills: [
        { title: 'Product design', text: 'Flows and interfaces for web and mobile.', level: 95 },
        { title: 'Brand identity', text: 'Logos, colours and type that feel like you.', level: 88 },
        { title: 'UI design systems', text: 'Reusable components your team can grow.', level: 92 },
        { title: 'Iconography', text: 'Custom icon sets in a consistent style.', level: 85 },
      ],
      why: 'You work directly with me from kick-off to launch, with no hand-offs. I design with developers in mind, so what you approve is what gets built, on time and on budget.',
      work: [
        { tag: 'Branding', title: 'Studio Nila rebrand', text: 'A fresh identity and website for a Chennai interior studio, launched in six weeks.' },
        { tag: 'Mobile app', title: 'Spendly wallet app', text: 'A simple budgeting app that helped 20,000 users track spending in their first month.' },
        { tag: 'Website', title: 'Tandoor House ordering site', text: 'A restaurant site with online ordering that doubled weekday takeaway orders.' },
      ],
      stats: [
        { value: '120+', label: 'Happy clients' },
        { value: '340+', label: 'Projects done' },
        { value: '12', label: 'Design awards' },
        { value: '8', label: 'Years experience' },
      ],
      testimonials: [
        { name: 'Priya Raman', title: 'Founder, Studio Nila', quote: 'Aarav understood our brand in one meeting. The new site looks beautiful and our enquiries went up straight away.' },
        { name: 'Daniel Brooks', title: 'CEO, Spendly', quote: 'Clear communication, great taste and always on time. He felt like part of our team from day one.' },
      ],
    },
  },
  ...SHOP_TEMPLATES,
  ...CREATIVE_TEMPLATES,
  {
    slug: 'fashion-store',
    name: 'Fashion Store',
    category: 'Shop',
    tagline: 'New season, new you.',
    description: 'A bold storefront for clothing brands with lookbooks and quick checkout.',
    colors: { primary: '#111111', accent: '#ff4f7b', bg: '#fff6f8' },
    hero: { title: 'Wear what moves you', subtitle: 'Fresh drops every Friday, free returns for 30 days.' },
    features: ['Free shipping over ₹999', '30-day easy returns', 'Secure Razorpay checkout'],
    items: [
      { name: 'Linen Overshirt', price: '₹1,899' },
      { name: 'Everyday Denim', price: '₹2,299' },
      { name: 'Knit Midi Dress', price: '₹2,799' },
      { name: 'Canvas Sneakers', price: '₹1,499' },
    ],
    popular: true,
  },
  {
    slug: 'grocery-mart',
    name: 'Grocery Mart',
    category: 'Shop',
    tagline: 'Fresh to your door.',
    description: 'Neighbourhood grocery with categories, offers and same-day delivery slots.',
    colors: { primary: '#1f7a3a', accent: '#ffb627', bg: '#f3fbf4' },
    hero: { title: 'Groceries in 30 minutes', subtitle: 'Farm-fresh produce and daily essentials, delivered fast.' },
    features: ['Same-day delivery', 'Farm-fresh produce', 'Weekly offers'],
    items: [
      { name: 'Organic Tomatoes', price: '₹60 / kg' },
      { name: 'Basmati Rice', price: '₹140 / kg' },
      { name: 'Cold-pressed Oil', price: '₹320' },
      { name: 'Seasonal Fruit Box', price: '₹499' },
    ],
    popular: true,
  },
  {
    slug: 'electronics-hub',
    name: 'Electronics Hub',
    category: 'Shop',
    tagline: 'Tech that keeps up.',
    description: 'Gadget store with specs-first product cards and comparison-friendly layout.',
    colors: { primary: '#0f172a', accent: '#22d3ee', bg: '#f1f7fb' },
    hero: { title: 'Upgrade your everyday tech', subtitle: 'Genuine products, brand warranty and no-cost EMI.' },
    features: ['Brand warranty', 'No-cost EMI', 'Express delivery'],
    items: [
      { name: 'Wireless Earbuds', price: '₹2,499' },
      { name: 'Smart Watch', price: '₹4,999' },
      { name: '10,000 mAh Power Bank', price: '₹1,299' },
      { name: 'Bluetooth Speaker', price: '₹3,199' },
    ],
  },
  {
    slug: 'jewellery-boutique',
    name: 'Jewellery Boutique',
    category: 'Shop',
    tagline: 'Crafted to be treasured.',
    description: 'Elegant boutique layout with large imagery and gold accents.',
    colors: { primary: '#3b2a1a', accent: '#c9a227', bg: '#fbf8f1' },
    hero: { title: 'Timeless pieces, made by hand', subtitle: 'Certified gold and silver, crafted in small batches.' },
    features: ['Certified hallmark', 'Lifetime polish', 'Gift wrapping'],
    items: [
      { name: 'Gold Hoop Earrings', price: '₹12,500' },
      { name: 'Silver Charm Bracelet', price: '₹3,400' },
      { name: 'Pearl Pendant', price: '₹8,900' },
      { name: 'Stacking Rings Set', price: '₹6,200' },
    ],
  },
  {
    slug: 'furniture-studio',
    name: 'Furniture Studio',
    category: 'Shop',
    tagline: 'Spaces you love to live in.',
    description: 'Calm, editorial furniture shop with room-by-room collections.',
    colors: { primary: '#2d3a32', accent: '#d08c60', bg: '#f7f4ef' },
    hero: { title: 'Furniture made for real homes', subtitle: 'Solid wood, honest prices and free assembly.' },
    features: ['Solid wood', 'Free assembly', '5-year warranty'],
    items: [
      { name: 'Oak Lounge Chair', price: '₹18,900' },
      { name: 'Walnut Coffee Table', price: '₹14,500' },
      { name: 'Linen Sofa', price: '₹46,000' },
      { name: 'Rattan Floor Lamp', price: '₹5,800' },
    ],
  },
  {
    slug: 'bakery-cafe',
    name: 'Bakery Café',
    category: 'Food',
    tagline: 'Baked fresh every morning.',
    description: 'Warm café site with menu, pre-orders and custom cake requests.',
    colors: { primary: '#5a3825', accent: '#f29e4c', bg: '#fff8f0' },
    hero: { title: 'Fresh bread, good coffee', subtitle: 'Order ahead and skip the queue, or book a custom cake.' },
    features: ['Order ahead', 'Custom cakes', 'Eggless options'],
    items: [
      { name: 'Sourdough Loaf', price: '₹220' },
      { name: 'Butter Croissant', price: '₹120' },
      { name: 'Chocolate Truffle Cake', price: '₹850' },
      { name: 'Cold Brew', price: '₹180' },
    ],
    popular: true,
  },
  {
    slug: 'fine-dining',
    name: 'Fine Dining',
    category: 'Food',
    tagline: 'An evening to remember.',
    description: 'Dark, elegant restaurant site with tasting menu and table booking.',
    colors: { primary: '#141414', accent: '#d4af37', bg: '#f6f4ef' },
    hero: { title: 'A seasonal tasting menu', subtitle: 'Seven courses, local produce, reserved seating every evening.' },
    features: ['Table reservations', 'Seasonal menu', 'Private dining'],
    items: [
      { name: 'Chef’s Tasting Menu', price: '₹4,500' },
      { name: 'Wine Pairing', price: '₹2,800' },
      { name: 'Vegetarian Tasting', price: '₹3,900' },
      { name: 'Private Table (8)', price: 'On request' },
    ],
  },
  {
    slug: 'consulting-firm',
    name: 'Consulting Firm',
    category: 'Business',
    tagline: 'Clarity for growing companies.',
    description: 'Professional services site with case studies and a lead-capture form.',
    colors: { primary: '#1e3a8a', accent: '#38bdf8', bg: '#f3f6fd' },
    hero: { title: 'Strategy that turns into results', subtitle: 'We help growing businesses plan, launch and scale.' },
    features: ['Growth strategy', 'Operations audit', 'Finance & compliance'],
    items: [
      { name: 'Discovery Workshop', price: 'From ₹25,000' },
      { name: 'Quarterly Advisory', price: 'From ₹90,000' },
      { name: 'Market Entry Study', price: 'On request' },
      { name: 'Process Automation', price: 'On request' },
    ],
    popular: true,
  },
  {
    slug: 'saas-startup',
    name: 'SaaS Startup',
    category: 'Business',
    tagline: 'Ship faster, together.',
    description: 'Gradient product landing page with features, pricing tiers and sign-up.',
    colors: { primary: '#5b4cff', accent: '#3d7bff', bg: '#f5f4ff' },
    hero: { title: 'The workspace your team will actually use', subtitle: 'Plan, track and ship work in one fast, simple tool.' },
    features: ['Real-time collaboration', 'Integrations', 'Enterprise security'],
    items: [
      { name: 'Starter', price: 'Free' },
      { name: 'Team', price: '₹499 / user' },
      { name: 'Business', price: '₹999 / user' },
      { name: 'Enterprise', price: 'Contact us' },
    ],
  },
  {
    slug: 'real-estate',
    name: 'Realty Homes',
    category: 'Business',
    tagline: 'Find a place to call home.',
    description: 'Property listings with locations, highlights and site-visit booking.',
    colors: { primary: '#0b3b3c', accent: '#14b8a6', bg: '#f0f8f7' },
    hero: { title: 'Homes in the neighbourhoods you love', subtitle: 'Verified listings, transparent pricing, free site visits.' },
    features: ['Verified listings', 'Free site visits', 'Home-loan help'],
    items: [
      { name: '2 BHK, Whitefield', price: '₹78 L' },
      { name: '3 BHK, Baner', price: '₹1.2 Cr' },
      { name: 'Villa, ECR', price: '₹2.4 Cr' },
      { name: 'Studio, Andheri', price: '₹65 L' },
    ],
  },
  {
    slug: 'creative-agency',
    name: 'Creative Agency',
    category: 'Creative',
    tagline: 'Ideas that get noticed.',
    description: 'High-contrast agency site with work showcase and services.',
    colors: { primary: '#0a0a0a', accent: '#a3e635', bg: '#f6f7f2' },
    hero: { title: 'We make brands impossible to ignore', subtitle: 'Branding, web and campaigns for ambitious teams.' },
    features: ['Brand identity', 'Web design', 'Campaigns'],
    items: [
      { name: 'Brand Sprint', price: '2 weeks' },
      { name: 'Website Build', price: '6 weeks' },
      { name: 'Launch Campaign', price: '4 weeks' },
      { name: 'Retainer', price: 'Monthly' },
    ],
    popular: true,
  },
  {
    slug: 'photographer',
    name: 'Photographer',
    category: 'Creative',
    tagline: 'Moments, beautifully kept.',
    description: 'Minimal portfolio with galleries, packages and enquiry form.',
    colors: { primary: '#222222', accent: '#e76f51', bg: '#faf7f5' },
    hero: { title: 'Stories told in light', subtitle: 'Weddings, portraits and brand shoots across India.' },
    features: ['Weddings', 'Portraits', 'Brand shoots'],
    items: [
      { name: 'Portrait Session', price: '₹8,000' },
      { name: 'Wedding Day', price: '₹85,000' },
      { name: 'Product Shoot', price: '₹15,000' },
      { name: 'Prints & Albums', price: 'From ₹2,500' },
    ],
  },
  {
    slug: 'fitness-studio',
    name: 'Fitness Studio',
    category: 'Beauty & Health',
    tagline: 'Stronger every week.',
    description: 'Energetic gym site with class schedule, trainers and memberships.',
    colors: { primary: '#111827', accent: '#f97316', bg: '#fff7f0' },
    hero: { title: 'Train with people who push you', subtitle: 'Strength, HIIT and yoga classes from 6 am to 10 pm.' },
    features: ['Certified trainers', 'Group classes', 'Diet plans'],
    items: [
      { name: 'Monthly Pass', price: '₹2,499' },
      { name: 'Quarterly Pass', price: '₹6,499' },
      { name: 'Personal Training', price: '₹1,000 / session' },
      { name: 'Yoga Pack (10)', price: '₹3,000' },
    ],
  },
  {
    slug: 'dental-clinic',
    name: 'Dental Clinic',
    category: 'Beauty & Health',
    tagline: 'Gentle care, bright smiles.',
    description: 'Clean clinic site with treatments, doctors and appointment booking.',
    colors: { primary: '#0e5a8a', accent: '#4fd1c5', bg: '#f1f9fc' },
    hero: { title: 'Dental care without the anxiety', subtitle: 'Experienced dentists, modern equipment, evening slots.' },
    features: ['Online booking', 'Painless treatments', 'Insurance support'],
    items: [
      { name: 'Check-up & Cleaning', price: '₹1,200' },
      { name: 'Teeth Whitening', price: '₹8,500' },
      { name: 'Root Canal', price: 'From ₹6,000' },
      { name: 'Clear Aligners', price: 'On consultation' },
    ],
  },
  {
    slug: 'online-academy',
    name: 'Online Academy',
    category: 'Services',
    tagline: 'Learn skills that pay.',
    description: 'Course marketplace with curriculum previews and enrolment checkout.',
    colors: { primary: '#312e81', accent: '#facc15', bg: '#f6f5ff' },
    hero: { title: 'Learn from people who do the work', subtitle: 'Live cohorts and self-paced courses with certificates.' },
    features: ['Live cohorts', 'Certificates', 'Mentor support'],
    items: [
      { name: 'Web Development', price: '₹4,999' },
      { name: 'Digital Marketing', price: '₹3,499' },
      { name: 'Data Analytics', price: '₹5,999' },
      { name: 'Spoken English', price: '₹1,999' },
    ],
  },
  {
    slug: 'travel-agency',
    name: 'Travel Agency',
    category: 'Services',
    tagline: 'Go somewhere new.',
    description: 'Destination-led travel site with packages and enquiry booking.',
    colors: { primary: '#0c4a6e', accent: '#fb923c', bg: '#f0f7fb' },
    hero: { title: 'Trips planned around you', subtitle: 'Handpicked stays, local guides and 24/7 support.' },
    features: ['Custom itineraries', 'Visa assistance', '24/7 support'],
    items: [
      { name: 'Goa Getaway (4N)', price: '₹18,999' },
      { name: 'Kerala Backwaters (5N)', price: '₹24,500' },
      { name: 'Bali Explorer (6N)', price: '₹56,000' },
      { name: 'Ladakh Road Trip (7N)', price: '₹38,000' },
    ],
  },
];

export const getTemplate = (slug) => TEMPLATES.find(t => t.slug === slug);
